import React, { type ReactNode, useRef, useEffect } from "react";
import {
  Box,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Flex,
  List,
  Spinner,
  useColorModeValue,
  Portal,
} from "@chakra-ui/react";
import { BiSearch } from "react-icons/bi";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { theme } from "src/styles/theme";

interface IInputSelectSearch {
  title: string;
  value: string;
  onChangeInput: React.ChangeEventHandler<HTMLInputElement> | undefined;
  placeholder?: string;
  isSelected?: string;
  height?: string;
  width?: string;
  disabled?: boolean;
  isRequirement?: boolean;
  onChangeIcon: React.MouseEventHandler<HTMLDivElement>;
  onClickSearchIcon: () => void;
  loading?: boolean;
  children?: ReactNode;
  isOportunity?: boolean;
  searchOnClick?: boolean;
}

const InputSelectSearch = ({
  title,
  value,
  onChangeInput,
  placeholder,
  isSelected,
  height,
  width,
  disabled,
  isRequirement,
  onChangeIcon,
  onClickSearchIcon,
  loading,
  children,
  isOportunity,
  searchOnClick = false,
}: IInputSelectSearch) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState<boolean>(false);
  const [dropdownPosition, setDropdownPosition] = React.useState({
    left: 0,
    top: 0,
    width: 0,
  });

  const primaryColor = useColorModeValue(theme.primary, theme.primary);
  const textColor = useColorModeValue(theme.text, theme.text);
  const disabledColor = useColorModeValue(theme.disable, theme.disable);
  const borderColor = useColorModeValue(theme.disable, theme.disable);

  // Função para calcular a posição do dropdown
  const calculateDropdownPosition = React.useCallback(() => {
    if (!wrapperRef.current) return { left: 0, top: 0, width: 0 };

    const rect = wrapperRef.current.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft =
      window.pageXOffset || document.documentElement.scrollLeft;

    // Verifica se está dentro de um modal
    const modalElement =
      wrapperRef.current.closest('[role="dialog"]') ||
      wrapperRef.current.closest(".chakra-modal__content") ||
      wrapperRef.current.closest('[data-testid="modal"]');

    let adjustedLeft = rect.left + scrollLeft;
    let adjustedTop = rect.bottom + scrollTop;
    let adjustedWidth = rect.width;

    // Se está dentro de um modal, ajusta as coordenadas
    if (modalElement) {
      const modalRect = modalElement.getBoundingClientRect();
      const modalScrollTop = modalElement.scrollTop || 0;
      const modalScrollLeft = modalElement.scrollLeft || 0;

      // Calcula a posição relativa ao viewport, não ao modal
      adjustedLeft = rect.left;
      adjustedTop = rect.bottom;
      adjustedWidth = rect.width;

      // Garante que o dropdown não saia da viewport
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (adjustedLeft + adjustedWidth > viewportWidth) {
        adjustedLeft = viewportWidth - adjustedWidth - 10;
      }

      if (adjustedLeft < 10) {
        adjustedLeft = 10;
        adjustedWidth = Math.min(adjustedWidth, viewportWidth - 20);
      }
    }

    return {
      left: Math.max(0, adjustedLeft),
      top: adjustedTop,
      width: adjustedWidth,
    };
  }, []);

  // Função personalizada para gerenciar cliques fora do componente e scroll
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    function handleScroll(event: Event) {
      const target = event.target as Element;

      // Verifica se o scroll está acontecendo dentro do dropdown
      if (
        dropdownRef.current &&
        (dropdownRef.current.contains(target) || dropdownRef.current === target)
      ) {
        return;
      }

      // Se o scroll é fora do dropdown, fecha
      setOpen(false);
    }

    function handleResize() {
      if (open) {
        const newPosition = calculateDropdownPosition();
        setDropdownPosition(newPosition);
      }
    }

    // Calcula a posição inicial
    const initialPosition = calculateDropdownPosition();
    setDropdownPosition(initialPosition);

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [open, calculateDropdownPosition]);

  React.useEffect(() => {
    if (isSelected) {
      setOpen(false);
    }
  }, [isSelected]);

  const handleSearchIconClick = () => {
    if (isSelected) {
      onChangeIcon({} as React.MouseEvent<HTMLDivElement>);
    } else {
      setOpen(!open);
      onClickSearchIcon();
    }
  };

  return (
    <Box ref={wrapperRef} width="100%" position="relative">
      <Flex alignItems="center">
        <Text fontSize="12px" color={textColor} ml="1">
          {isRequirement && (
            <Text as="span" color="red.500" fontWeight="bold">
              *
            </Text>
          )}
          {title}
        </Text>
      </Flex>

      <InputGroup size="md" opacity={disabled ? 0.5 : 1}>
        <Input
          value={value}
          onChange={onChangeInput}
          placeholder={placeholder}
          isDisabled={disabled}
          readOnly={disabled}
          borderRadius="8px"
          borderWidth={2}
          borderColor={borderColor}
          focusBorderColor={primaryColor}
          _focus={{ borderColor: primaryColor, borderWidth: "1.5px" }}
          _hover={{ color: "none" }}
          height="44px"
          fontSize="13px"
          fontWeight="bold"
          onClick={() => {
            if (searchOnClick) {
              setOpen(true);
              onClickSearchIcon();
            }
          }}
          _placeholder={{ fontWeight: "normal" }}
        />
        <InputRightElement width={isSelected ? "40px" : "44px"} height="44px">
          {isSelected ? (
            <Box
              cursor={disabled ? "not-allowed" : "pointer"}
              color={textColor}
              onClick={disabled ? undefined : onChangeIcon}
              fontSize="22px"
            >
              <AiOutlineCloseCircle />
            </Box>
          ) : (
            <Flex
              alignItems="center"
              justifyContent="center"
              bg={disabled ? disabledColor : primaryColor}
              height="100%"
              width="100%"
              borderRightRadius="7px"
              cursor={disabled ? "not-allowed" : "pointer"}
              onClick={disabled ? undefined : handleSearchIconClick}
            >
              <BiSearch color="white" size={22} />
            </Flex>
          )}
        </InputRightElement>
      </InputGroup>

      {open && (
        <Portal>
          <Box
            ref={dropdownRef}
            position="fixed"
            left={`${dropdownPosition.left}px`}
            top={`${dropdownPosition.top}px`}
            width={`${dropdownPosition.width}px`}
            maxHeight={height || "170px"}
            minHeight="50px"
            bg="white"
            borderRadius="8px"
            border="1px solid"
            borderColor={borderColor}
            boxShadow="lg"
            zIndex="99999"
            overflowY="auto"
            onScroll={(e) => e.stopPropagation()}
          >
            {loading ? (
              <Flex
                justifyContent="center"
                alignItems="center"
                py="4"
                minHeight="50px"
              >
                <Spinner color={primaryColor} size="md" />
              </Flex>
            ) : (
              <List p="2" width="100%">
                {children}
              </List>
            )}
          </Box>
        </Portal>
      )}
    </Box>
  );
};

export default InputSelectSearch;
